/**
 * `/about-us/news-events/<eventSlug>/`: one Ryerson event page, built for each upcoming event in
 * Contentful. Only the Ryerson build has these pages.
 *
 * `getStaticPaths` takes the slugs from `src/contentful/event/getContentfulUpcomingEventsSlugs.ts`:
 * events dated no earlier than two days before the build. An event's page leaves the export at
 * the first build after that, and its URL becomes a 404. `getStaticProps` fetches the event in
 * every language (`getContentfulEventDetails.ts`, which fails the build when a language has no
 * entry), the upcoming-events list for the sidebar, and the blog entries for "Related Posts".
 * The page renders `components/NewsEvents/Events/EventDetailsContainer.tsx`.
 *
 * In this repo, event links are built by `components/NewsEvents/Events/UpcomingEventsSection.tsx`
 * on the News & Events and About Us pages. Pinned by
 * `src/unit-test/pages/about-us/news-events/eventSlug.spec.tsx`.
 */
import EventDetailsContainer from "@components/NewsEvents/Events/EventDetailsContainer";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import { getContentfulBlogEntries } from "@contentful/blog/getContentfulBlogEntries";
import { TranslatedBlogEntries } from "@contentful/blog/types";
import getContentfulEventDetails from "@contentful/event/getContentfulEventDetails";
import getContentfulUpcomingEvents from "@contentful/event/getContentfulUpcomingEvents";
import getContentfulUpcomingEventsSlugs from "@contentful/event/getContentfulUpcomingEventsSlugs";
import {
	ProcessedContentfulEventDetailsFieldsByLanguage,
	ProcessedContentfulUpcomingEventsByLanguage,
} from "@contentful/event/types";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { GetStaticProps } from "next";
import React from "react";

/**
 * One path per upcoming event slug on the Ryerson build, none elsewhere; any other slug is a 404.
 */
export const getStaticPaths = async () => {
	// this will prevent pages from being built if the company is not ryerson
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		// If company is not ryerson, in local dev (yarn start) next.js will still try to serve the page for any URL but since getStaticProps will not run, the page will break.
		// In production (static export), unknown paths return 404.html — so this is a local dev-only quirk.
		return { paths: [], fallback: false };
	}

	const eventSlugs = await getContentfulUpcomingEventsSlugs();

	// If `eventSlugs` is an empty [], local dev (`yarn start`) will try to serve the page for any URL but since getStaticProps will not run, the page will break.
	// In production (static export), unknown paths return 404.html — so this is a local dev-only quirk.
	return {
		paths: eventSlugs.map((eventSlug) => ({
			params: {
				eventSlug,
			},
		})),
		fallback: false, // returns 404 for all other paths
	};
};

type PageParams = {
	eventSlug: string;
};

type PageProps = {
	contentfulEventDetailsByLanguage: ProcessedContentfulEventDetailsFieldsByLanguage;
	allUpcomingEventsByLanguage: ProcessedContentfulUpcomingEventsByLanguage;
	contentfulBlogEntries: TranslatedBlogEntries;
};

/**
 * Build-time props for one event page. No brand check here: it runs only for the paths above.
 * Each fetcher rethrows, so a Contentful failure fails the build.
 */
export const getStaticProps: GetStaticProps<PageProps, PageParams> = async ({ params }) => {
	const { props: headerFooterProps } = await allContentfulHeaderCalls();
	return {
		props: {
			...headerFooterProps,
			contentfulEventDetailsByLanguage: await getContentfulEventDetails(
				params?.eventSlug as string
			), // type narrowing as we know this is a string
			allUpcomingEventsByLanguage: await getContentfulUpcomingEvents(),
			contentfulBlogEntries: await getContentfulBlogEntries(),
		},
	};
};

/** Renders the event page inside the site header and footer. */
const Page: React.FC<HeaderPageProps & PageProps> = (props) => {
	return (
		<HeaderLayout {...getHeaderData(props)}>
			<EventDetailsContainer
				contentfulEventDetailsByLanguage={props.contentfulEventDetailsByLanguage}
				allUpcomingEventsByLanguage={props.allUpcomingEventsByLanguage}
				contentfulBlogEntries={props.contentfulBlogEntries}
			/>
		</HeaderLayout>
	);
};

export default Page;
